<script setup>
/**
 * Desktop Icon Component
 * A draggable icon that represents a shortcut on the desktop
 * Adapts to dynamic grid cell sizes
 */

import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { ICON_SVG_PATHS, GRID_CONFIG, getCellPosition } from '@/core/config/desktopShortcuts'
import { useConfirmDialog } from '@/core/composables/useConfirmDialog'

const props = defineProps({
  shortcut: {
    type: Object,
    required: true,
  },
  iconSize: {
    type: Number,
    default: 52,
  },
  cellWidth: {
    type: Number,
    default: 95,
  },
  cellHeight: {
    type: Number,
    default: 105,
  },
  canvasWidth: {
    type: Number,
    default: 800,
  },
  canvasHeight: {
    type: Number,
    default: 600,
  },
})

const emit = defineEmits(['drag-end', 'remove', 'drag-start', 'drag-move'])

const router = useRouter()
const { confirm } = useConfirmDialog()

// Local position for smooth dragging
const localPosition = ref({ x: 0, y: 0 })

// Drag state
const isDragging = ref(false)
const dragStart = ref({ x: 0, y: 0 })
const mouseDownPos = ref({ x: 0, y: 0 })
const hasDragStarted = ref(false)

// Drag threshold - must move this many pixels before drag starts
const DRAG_THRESHOLD = 8

// Get SVG path for icon
const iconPath = computed(() => {
  return ICON_SVG_PATHS[props.shortcut.icon] || ICON_SVG_PATHS['portal']
})

// Calculate position from grid cell
const calculatedPosition = computed(() => {
  if (props.shortcut.gridCol !== undefined && props.shortcut.gridRow !== undefined) {
    return getCellPosition(
      props.shortcut.gridCol,
      props.shortcut.gridRow,
      props.canvasWidth,
      props.canvasHeight
    )
  }
  return props.shortcut.position || { x: 50, y: 50 }
})

// Position style
const positionStyle = computed(() => {
  const pos = isDragging.value ? localPosition.value : calculatedPosition.value
  return {
    left: `${pos.x}px`,
    top: `${pos.y}px`,
    zIndex: isDragging.value ? 1000 : 1,
  }
})

// Dynamic icon style based on cell size
const iconImageStyle = computed(() => ({
  width: `${props.iconSize}px`,
  height: `${props.iconSize}px`,
  borderRadius: `${Math.max(10, props.iconSize * 0.22)}px`,
}))

// Sync position when props change
function syncPosition() {
  if (!isDragging.value) {
    localPosition.value = { ...calculatedPosition.value }
  }
}

watch(
  calculatedPosition,
  () => {
    syncPosition()
  },
  { immediate: true }
)

// Mouse handlers
function handleMouseDown(e) {
  if (e.button !== 0) return

  e.preventDefault()

  // Store initial mouse position for threshold check
  mouseDownPos.value = { x: e.clientX, y: e.clientY }
  hasDragStarted.value = false
  localPosition.value = { ...calculatedPosition.value }

  dragStart.value = {
    x: e.clientX - localPosition.value.x,
    y: e.clientY - localPosition.value.y,
  }

  document.addEventListener('mousemove', handleMouseMove)
  document.addEventListener('mouseup', handleMouseUp)
}

function handleMouseMove(e) {
  e.preventDefault()

  // Check if we've moved past the threshold to start dragging
  if (!hasDragStarted.value) {
    const dx = e.clientX - mouseDownPos.value.x
    const dy = e.clientY - mouseDownPos.value.y
    const distance = Math.sqrt(dx * dx + dy * dy)

    if (distance < DRAG_THRESHOLD) {
      return // Not moved enough, don't start drag yet
    }

    // Start dragging
    hasDragStarted.value = true
    isDragging.value = true
    emit('drag-start', props.shortcut.id)
  }

  let newX = e.clientX - dragStart.value.x
  let newY = e.clientY - dragStart.value.y

  // More permissive bounds - allow positioning near edges
  const padding = GRID_CONFIG.PADDING
  newX = Math.max(padding, Math.min(newX, props.canvasWidth - padding - 20))
  newY = Math.max(padding, Math.min(newY, props.canvasHeight - padding - 20))

  localPosition.value = { x: newX, y: newY }
  emit('drag-move', props.shortcut.id, { x: newX, y: newY })
}

function handleMouseUp(e) {
  document.removeEventListener('mousemove', handleMouseMove)
  document.removeEventListener('mouseup', handleMouseUp)

  // If we actually dragged, emit drag-end
  if (hasDragStarted.value && isDragging.value) {
    isDragging.value = false
    hasDragStarted.value = false
    emit('drag-end', props.shortcut.id, { ...localPosition.value })
  } else {
    // It was just a click, not a drag - navigate
    hasDragStarted.value = false
    if (props.shortcut.routeName) {
      router.push({ name: props.shortcut.routeName })
    }
  }
}

// Touch handlers
function handleTouchStart(e) {
  if (e.touches.length !== 1) return

  const touch = e.touches[0]

  // Store initial touch position for threshold check
  mouseDownPos.value = { x: touch.clientX, y: touch.clientY }
  hasDragStarted.value = false
  localPosition.value = { ...calculatedPosition.value }

  dragStart.value = {
    x: touch.clientX - localPosition.value.x,
    y: touch.clientY - localPosition.value.y,
  }

  document.addEventListener('touchmove', handleTouchMove, { passive: false })
  document.addEventListener('touchend', handleTouchEnd)
  document.addEventListener('touchcancel', handleTouchEnd)
}

function handleTouchMove(e) {
  const touch = e.touches[0]

  // Check if we've moved past the threshold to start dragging
  if (!hasDragStarted.value) {
    const dx = touch.clientX - mouseDownPos.value.x
    const dy = touch.clientY - mouseDownPos.value.y
    const distance = Math.sqrt(dx * dx + dy * dy)

    if (distance < DRAG_THRESHOLD) {
      return // Not moved enough, don't start drag yet
    }

    // Start dragging
    hasDragStarted.value = true
    isDragging.value = true
    emit('drag-start', props.shortcut.id)
  }

  e.preventDefault()

  let newX = touch.clientX - dragStart.value.x
  let newY = touch.clientY - dragStart.value.y

  // More permissive bounds - allow positioning near edges
  const padding = GRID_CONFIG.PADDING
  newX = Math.max(padding, Math.min(newX, props.canvasWidth - padding - 20))
  newY = Math.max(padding, Math.min(newY, props.canvasHeight - padding - 20))

  localPosition.value = { x: newX, y: newY }
  emit('drag-move', props.shortcut.id, { x: newX, y: newY })
}

function handleTouchEnd(e) {
  document.removeEventListener('touchmove', handleTouchMove)
  document.removeEventListener('touchend', handleTouchEnd)
  document.removeEventListener('touchcancel', handleTouchEnd)

  // If we actually dragged, emit drag-end
  if (hasDragStarted.value && isDragging.value) {
    isDragging.value = false
    hasDragStarted.value = false
    emit('drag-end', props.shortcut.id, { ...localPosition.value })
  } else {
    // It was just a tap, not a drag - navigate
    hasDragStarted.value = false
    if (props.shortcut.routeName) {
      router.push({ name: props.shortcut.routeName })
    }
  }
}

// Right click to delete
async function handleContextMenu(e) {
  e.preventDefault()
  const ok = await confirm({
    title: 'מחיקת קיצור דרך',
    message: `האם למחוק את "${props.shortcut.label}"?`,
    confirmText: 'מחק',
    variant: 'danger',
  })
  if (ok) emit('remove', props.shortcut.id)
}

// Cleanup
onUnmounted(() => {
  document.removeEventListener('mousemove', handleMouseMove)
  document.removeEventListener('mouseup', handleMouseUp)
  document.removeEventListener('touchmove', handleTouchMove)
  document.removeEventListener('touchend', handleTouchEnd)
  document.removeEventListener('touchcancel', handleTouchEnd)
})

defineExpose({ syncPosition })
</script>

<template>
  <div
    class="desktop-icon"
    :class="{ dragging: isDragging }"
    :style="positionStyle"
    @mousedown="handleMouseDown"
    @touchstart.prevent="handleTouchStart"
    @contextmenu="handleContextMenu"
  >
    <div class="icon-image" :style="iconImageStyle">
      <svg
        viewBox="0 0 24 24"
        fill="currentColor"
        :style="{ width: `${iconSize * 0.5}px`, height: `${iconSize * 0.5}px` }"
      >
        <path :d="iconPath" />
      </svg>
    </div>
    <span class="icon-label" :style="{ width: `${cellWidth - 8}px` }">{{ shortcut.label }}</span>
  </div>
</template>

<style scoped>
.desktop-icon {
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.3rem;
  padding: 0.4rem;
  cursor: pointer;
  user-select: none;
  transition: box-shadow 0.15s ease;
  touch-action: none;
  border-radius: 10px;
}

.desktop-icon:hover {
  background: rgba(0, 0, 0, 0.05);
}

.desktop-icon.dragging {
  cursor: grabbing;
  transform: scale(1.08);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  background: rgba(255, 255, 255, 0.9);
}

.icon-image {
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  box-shadow: 0 4px 14px rgba(59, 130, 246, 0.3);
  transition:
    transform 0.15s ease,
    box-shadow 0.15s ease;
}

.desktop-icon:hover .icon-image {
  transform: scale(1.05);
  box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
}

.desktop-icon.dragging .icon-image {
  transform: scale(1);
}

.icon-label {
  color: #1e293b;
  font-size: 0.65rem;
  font-weight: 500;
  text-align: center;
  text-shadow: 0 1px 2px rgba(255, 255, 255, 0.8);
  line-height: 1.1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
